/* eslint-disable react/jsx-max-props-per-line */
/* eslint-disable react/jsx-sort-props */
import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Skeleton,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import {
  ChartEmpty,
  buildMaterialBarSeries,
  buildPeriodoHistoricoModel,
  getMaterialChartLegendProps,
} from './chartShared';
import {
  PERIODO_CHART_MODE_GROUPED,
  PERIODO_CHART_MODE_STACKED,
  formatKgMetric,
} from './constants';

const PERIODO_X_AXIS_ID = 'periodo-x-axis';
const HISTORICO_CHART_HEIGHT = 360;

const formatTooltipValue = (value, label) => {
  const kg = formatKgMetric(value);
  return label ? `${label}: ${kg} kg` : `${kg} kg`;
};

const PeriodoHistoricoChart = ({
  items,
  materialColumns,
  loading,
  height,
}) => {
  const theme = useTheme();
  const [mode, setMode] = useState(PERIODO_CHART_MODE_STACKED);

  const { dataset, materialColumns: resolvedColumns } = useMemo(
    () => buildPeriodoHistoricoModel(items, materialColumns),
    [items, materialColumns],
  );

  const series = useMemo(
    () => buildMaterialBarSeries(resolvedColumns, mode, theme, formatTooltipValue),
    [resolvedColumns, mode, theme],
  );

  const chartHeight = height || HISTORICO_CHART_HEIGHT;
  const hasData = dataset.length > 0;
  const axisLabelColor = theme.palette.text.secondary;

  if (loading) {
    return <Skeleton variant="rectangular" height={chartHeight} sx={{ borderRadius: 1 }} />;
  }

  if (!hasData) {
    return <ChartEmpty message="Sem histórico de pesagem nos últimos períodos" />;
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          mb: 1.5,
        }}
      >
        <ToggleButtonGroup
          value={mode}
          exclusive
          size="small"
          onChange={(_, next) => {
            if (next) setMode(next);
          }}
          aria-label="Modo do gráfico por período"
        >
          <ToggleButton value={PERIODO_CHART_MODE_GROUPED} sx={{ textTransform: 'none', px: 2 }}>
            Agrupado
          </ToggleButton>
          <ToggleButton value={PERIODO_CHART_MODE_STACKED} sx={{ textTransform: 'none', px: 2 }}>
            Empilhado
          </ToggleButton>
        </ToggleButtonGroup>
        <Typography variant="caption" color="text.secondary">
          Semana atual e 10 anteriores — independente do período selecionado nos filtros
        </Typography>
      </Box>

      <BarChart
        height={chartHeight}
        dataset={dataset}
        xAxis={[
          {
            id: PERIODO_X_AXIS_ID,
            scaleType: 'band',
            dataKey: 'periodoAxisLabel',
            height: 'auto',
            labelStyle: {
              fill: axisLabelColor,
              fontSize: 11,
              fontWeight: 500,
            },
            tickLabelStyle: {
              fill: axisLabelColor,
              fontSize: 10,
              textAnchor: 'middle',
            },
          },
        ]}
        yAxis={[{ label: 'kg', min: 0 }]}
        series={series}
        margin={{ left: 56, right: 16, top: 8, bottom: 24 }}
        slotProps={{
          legend: getMaterialChartLegendProps(),
        }}
        sx={{ width: '100%' }}
      />
    </Box>
  );
};

PeriodoHistoricoChart.propTypes = {
  items: PropTypes.array,
  materialColumns: PropTypes.array,
  loading: PropTypes.bool,
  height: PropTypes.number,
};

PeriodoHistoricoChart.defaultProps = {
  items: [],
  materialColumns: [],
  loading: false,
  height: null,
};

export default PeriodoHistoricoChart;
